// Builder-notes price lines (DESIGN.md 4.4, TOOLS.md 4.3). The model cites a
// line from the raw builder notes and the figure on it. Code checks the text
// is really in the notes, that the figure is a lump sum written on that line,
// and reads the GST basis from the line itself. The model never supplies a
// number the notes do not contain.

const ws = (s) => String(s || '').replace(/\r\n?/g, '\n').replace(/[ \t ]+/g, ' ');

// QUESTIONS.md Q10's working rule (2026-10-28, Francis to confirm). A price
// marked on its own line is read that way (ex GST when the line carries both
// marks). Any other price follows the notes: as their total is marked (the
// builder's line figures roll up to that total), inc GST before ex GST;
// else ex GST where the notes mark prices ex GST and none inc GST; else inc
// GST. Notes that mark GST both ways can still mislead it, so where they give
// a total the notes-total-gap flag says so. Every priced-notes job
// whose sent quote follows the notes reads them inc GST. The old default (a
// price nothing marks is ex GST) priced four C2 jobs 9% to 10.5% high
// (results/C2.md), against the rule that the stated price is the one printed.
// "inc GST", "incl. GST", "including GST", "inc-GST", "includes GST",
// "GST incl", "GST inclusive".
const INC_GST_RE = /\binc(?:l(?:uding|usive)?)?\.?[\s-]*(?:of\s+)?gst\b|\binclud(?:e|es|ed)\s+gst\b|\bgst[\s-]*inc(?:l(?:uded|usive)?)?\b/i;
// "ex GST", "ex-GST", "exc GST", "excl. GST", "excluding" or "excludes GST",
// "GST excl", "GST exclusive", "GST extra", "GST to be added", "before GST",
// "nett of GST", "+ GST", "plus 10% GST". Now that a price nothing marks is
// inc GST, an ex mark this misses prints the price 9% low.
const EX_GST_RE = /\bex(?:c(?:l(?:uding|usive|udes?)?)?)?\.?[\s-]*(?:of\s+)?gst\b|\bgst[\s-]*ex(?:c(?:l(?:uded|usive)?)?)?\b|\bgst\s+(?:10\s*%\s+)?(?:extra|to\s+be\s+added)\b|\bbefore\s+gst\b|\bnett?\s+of\s+gst\b|(?:\+|\bplus)\s*(?:10\s*%\s*)?gst\b/i;
const UNIT_AFTER_RE = /^\s*(?:%|x\b|sq\s?m\b|sqm\b|sm\b|m2\b|m²|mm\b|lm\b|m\b|men\b|man\b|days?\b|hrs?\b|hours?\b|weeks?\b|kg\b|lt?\b|litres?\b|ea\b|each\b|storeys?\b|steps?\b|balcon)/i;
const PER_UNIT_AFTER_RE = /^\s*\/\s*[a-z]/i;

/**
 * The notes line holding `cited` (whitespace-normalised), with the cited
 * span's position in it. A citation that spans a wrapped line is matched
 * against the joined notes, and that joined text is the line.
 */
export function findNotesLine(notesText, cited) {
    const want = ws(cited).trim();
    if (!want) return null;
    const lines = ws(notesText).split('\n');
    const full = lines.find((l) => l.includes(want));
    if (full !== undefined) return { line: full, cited: want, start: full.indexOf(want) };
    // A citation spanning lines: its "line" is only the lines it overlaps, so
    // a marker elsewhere in the notes cannot change how its figure is read.
    const joined = lines.join(' ');
    const at = joined.indexOf(want);
    if (at < 0) return null;
    let offset = 0;
    let first = -1;
    let last = -1;
    let firstOffset = 0;
    lines.forEach((l, i) => {
        const end = offset + l.length;
        if (first < 0 && end > at) { first = i; firstOffset = offset; }
        if (offset < at + want.length) last = i;
        offset = end + 1;
    });
    const span = lines.slice(first, last + 1).join(' ');
    return { line: span, cited: want, start: at - firstOffset };
}

/** Lump-sum figures written on a piece of notes text, with their spans. */
export function lumpSumFigures(text) {
    const out = [];
    const re = /(\$\s?)?(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?/g;
    let m;
    while ((m = re.exec(text)) !== null) {
        const dollar = Boolean(m[1]);
        let value = Number(`${m[2].replace(/,/g, '')}${m[3] || ''}`);
        let end = m.index + m[0].length;
        let after = text.slice(end);
        if (/^k\b/i.test(after)) { value *= 1000; after = after.slice(1); end += 1; }
        const before = text.slice(Math.max(0, m.index - 1), m.index);
        const span = { start: m.index, end };
        if (/[\d.x×]/i.test(before) && !dollar) continue; // part of a dimension such as 8.2m X 3.9m
        if (PER_UNIT_AFTER_RE.test(after)) { out.push({ value, dollar, perUnit: true, ...span }); continue; }
        if (!dollar && (UNIT_AFTER_RE.test(after) || value < 100)) continue;
        if (/^\s*%/.test(after)) continue;
        out.push({ value, dollar, perUnit: false, ...span });
    }
    return out;
}

/** The basis a notes line is read on, and why (recorded on every lock). */
export function notesLineBasis(line, notesText = '') {
    if (EX_GST_RE.test(line)) return { basis: 'ex', basisReason: 'the line is marked ex GST' };
    if (INC_GST_RE.test(line)) return { basis: 'inc', basisReason: 'the line is marked inc GST' };
    const notesLines = ws(notesText).split('\n');
    const quote = (l) => `("${l.trim().slice(0, 80)}")`;
    const isTotal = (l) => /\btotal\b/i.test(l);
    const incTotal = notesLines.find((l) => isTotal(l) && INC_GST_RE.test(l) && !EX_GST_RE.test(l));
    if (incTotal) return { basis: 'inc', basisReason: `the notes mark their total inc GST ${quote(incTotal)}` };
    const exTotal = notesLines.find((l) => isTotal(l) && EX_GST_RE.test(l) && !INC_GST_RE.test(l));
    if (exTotal) return { basis: 'ex', basisReason: `the notes mark their total ex GST ${quote(exTotal)}` };
    const markedEx = notesLines.find((l) => EX_GST_RE.test(l));
    if (!markedEx) return { basis: 'inc', basisReason: 'nothing in the notes marks it ex GST' };
    if (!notesLines.some((l) => INC_GST_RE.test(l))) return { basis: 'ex', basisReason: `the notes mark prices ex GST ${quote(markedEx)} and none inc GST` };
    return { basis: 'inc', basisReason: 'its line is not marked, and the notes mark prices both inc and ex GST' };
}

/**
 * Check one `notes` build-up line. Returns { ok, line, figure, basis, exGst }
 * or { ok: false, error, message }.
 */
export function verifyNotesLine(notesText, { line, figure }) {
    if (!notesText) return { ok: false, error: 'no-builder-notes', message: 'This job has no builder notes, so no line can be priced from them.' };
    const found = findNotesLine(notesText, line);
    if (!found) return { ok: false, error: 'notes-line-not-found', message: 'That text is not in the builder notes. Copy the line exactly as written.' };
    const value = Number(figure);
    if (!(value > 0)) return { ok: false, error: 'notes-figure-missing', message: 'Give the figure from the line that this item uses.' };
    // Figures are read from the whole line and must lie wholly inside the
    // cited span, so a citation cannot cut "$1200" down to "$120".
    const spanEnd = found.start + found.cited.length;
    const figures = lumpSumFigures(found.line).filter((f) => f.start >= found.start && f.end <= spanEnd);
    const hit = figures.find((f) => Math.abs(f.value - value) < 0.005);
    if (!hit) {
        const listed = figures.filter((f) => !f.perUnit).map((f) => f.value);
        return { ok: false, error: 'notes-figure-not-on-line', message: `The figure ${value} is not a lump sum written on that line.${listed.length ? ` Figures on it: ${listed.join(', ')}.` : ''}` };
    }
    if (hit.perUnit) return { ok: false, error: 'notes-figure-per-unit', message: `${value} is a rate per unit, not a lump sum. Use the line's total, or a rate-book line with a quantity.` };
    const { basis, basisReason } = notesLineBasis(found.line, notesText);
    return {
        ok: true,
        line: found.cited,
        fullLine: found.line.trim(),
        figure: value,
        basis,
        basisReason,
        exGst: Math.round((basis === 'inc' ? value / 1.1 : value) * 100) / 100,
        isTotalLine: /\b(?:sub)?total\b/i.test(found.line),
    };
}

/**
 * B6 'detail' (B5.md U7): the notes give their total and no line of theirs
 * names HBCF, so the total leaves it out. "HBC" alone is the home building
 * contract, not the insurance.
 */
export function notesTotalLeavesOutHbcf(notesText) {
    return notesTotalLines(notesText).length > 0 && !/\bhbcf\b|\bhbc\s+f\b|\bhome building compensation|\bhbc\s+insurance/i.test(String(notesText || ''));
}

/** Total lines in the notes, for the notes-total-gap flag. */
export function notesTotalLines(notesText) {
    return ws(notesText).split('\n')
        .map((l) => l.trim())
        .filter((l) => /\btotal\b/i.test(l) && !/\bsub\s?total\b/i.test(l))
        .map((l) => {
            const figs = lumpSumFigures(l).filter((f) => !f.perUnit);
            const last = figs[figs.length - 1];
            return last ? { line: l, value: last.value, basis: notesLineBasis(l, notesText).basis } : null;
        })
        .filter(Boolean);
}
